import { useEffect, useCallback } from 'react';
import { refreshTokenIfNeeded } from '../services/auth';
import { logger } from '../services/analytics';
import { isNetworkError, isSessionExpiredError } from '../utils/errors';
import { AUTH_CONSTANTS } from '../utils/auth-constants';
import { isProtectedRoute, isJobDetailRoute } from '../utils/route-utils';

export const useTokenRefresh = () => {
  const checkAndRefreshToken = useCallback(async () => {
    try {
      await refreshTokenIfNeeded();
    } catch (error: unknown) {
      // Sessão expirada é um comportamento esperado, não um erro crítico
      if (isSessionExpiredError(error)) {
        // Apenas logar em modo debug (não reportar ao Sentry)
        logger.auth('Sessão expirada durante verificação de token (comportamento esperado)', {
          reason: (error as any).reason,
          isSessionExpired: true
        });
        
        // Tokens já foram limpos em refreshAccessToken, não precisamos limpar novamente
        // Apenas garantir que não há tokens residuais
        
        // Redirecionar para login se estiver em rota protegida
        // Isso mantém o comportamento de refresh automático silencioso,
        // mas garante que o usuário seja redirecionado quando a sessão expirar
        if (typeof window !== 'undefined') {
          const currentPath = window.location.pathname;
          
          if (isProtectedRoute(currentPath)) {
            // Salvar flag para redirecionamento de ofertas se necessário
            if (isJobDetailRoute(currentPath)) {
              sessionStorage.setItem(AUTH_CONSTANTS.SESSION_STORAGE.REDIRECT_FROM_OFFERS, 'true');
            }
            
            // Redirecionar silenciosamente para login
            window.location.href = '/login';
          }
        }
        
        return;
      }
      
      // Distinguir entre erros de rede e erros de autenticação usando helper function
      if (isNetworkError(error)) {
        // Para erros de rede, apenas logar - não fazer logout
        // O token pode ainda ser válido, apenas houve problema temporário de conexão
        logger.error('Erro de rede ao verificar/renovar token (preservando sessão)', error as Error, {
          isNetworkError: true,
          preserveTokens: true,
          httpStatus: (error as any)?.httpStatus,
          code: (error as any)?.code
        });
      } else {
        // Para outros erros (autenticação, etc), logar normalmente
        logger.error('Erro ao verificar/renovar token', error as Error);
      }
    }
  }, []);

  useEffect(() => {
    // Verificar token a cada 5 minutos
    const interval = setInterval(checkAndRefreshToken, 5 * 60 * 1000);
    
    // Verificar token imediatamente ao montar o componente
    checkAndRefreshToken();

    return () => clearInterval(interval);
  }, [checkAndRefreshToken]);

  return { checkAndRefreshToken };
};
